import { createTheme, responsiveFontSizes } from "@mui/material/styles";

let theme = createTheme({
  palette: {
    primary: {
      main: "#000000",
    },
    secondary: {
      main: "#eebe25",
    },
    background: {
      default: "#F5F5F5",
      paper: "#FFFFFF",
    },
    error: {
      main: "#D32F2F",
    },
  },
  typography: {
    fontFamily: "'Poppins', sans-serif",
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          WebkitTextSizeAdjust: "100%",
          WebkitTapHighlightColor: "transparent",
        },
        body: {
          overscrollBehaviorY: "none",
        },
        // Make any plain <table> usable on small screens
        "table": {
          maxWidth: "100%",
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            paddingLeft: theme.spacing(1.5),
            paddingRight: theme.spacing(1.5),
          },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          [theme.breakpoints.down("sm")]: {
            borderRadius: 10,
          },
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1.25),
            "&:last-child": {
              paddingBottom: theme.spacing(1.25),
            },
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    // Loading placeholders shimmer — a highlight sweeping across, like
    // YouTube's — rather than MUI's default slow pulse, which was too faint
    // to read as "loading" on a white card. Stock "wave" is also faint, so
    // the base is darker and the sweep brighter and quicker.
    MuiSkeleton: {
      defaultProps: {
        animation: "wave",
      },
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0.09)",
        },
        wave: {
          "&::after": {
            background:
              "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.75), transparent)",
            animationDuration: "1.4s",
          },
          "@media (prefers-reduced-motion: reduce)": {
            "&::after": { animation: "none" },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backdropFilter: "saturate(180%) blur(8px)",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            minHeight: 56,
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 40,
          [theme.breakpoints.down("sm")]: {
            minHeight: 38,
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "none",
          fontWeight: 600,
          minHeight: 40,
          [theme.breakpoints.down("sm")]: {
            minHeight: 38,
            minWidth: "auto",
            fontSize: "0.85rem",
            paddingLeft: theme.spacing(1.25),
            paddingRight: theme.spacing(1.25),
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            padding: 8,
          },
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "none",
          [theme.breakpoints.down("sm")]: {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            fontSize: "0.8rem",
          },
        }),
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          width: "100%",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            "& .MuiTableCell-root": {
              paddingLeft: 8,
              paddingRight: 8,
              fontSize: "0.8125rem",
            },
            "& .MuiTableCell-head": {
              fontSize: "0.75rem",
            },
          },
        }),
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            paddingLeft: 8,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: "0.75rem",
            },
          },
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(1.5),
            width: `calc(100% - ${theme.spacing(3)})`,
            maxWidth: `calc(100% - ${theme.spacing(3)})`,
          },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2),
          },
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: "none",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          marginLeft: theme.spacing(1),
          marginRight: theme.spacing(1),
          paddingLeft: theme.spacing(1.5),
          paddingRight: theme.spacing(1.5),
          "&.Mui-selected": {
            backgroundColor: "rgba(0,0,0,0.06)",
          },
        }),
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterTouchDelay: 200,
        leaveTouchDelay: 4000,
      },
    },
  },
});

theme = responsiveFontSizes(theme, { factor: 2.4 });

export default theme;
